"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MapPin, Route, Bell, Wrench, CreditCard, ChartBar, ArrowRight, ShieldCheck, Zap } from "lucide-react";
const services = [{
  id: 1,
  title: "Professional Skills Academy",
  description: "Empower your workforce with our signature soft skills and leadership programs. Since 2017, we have been refining professional excellence through interactive, high-impact training modules.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/professional_skills_training_session.png"
}, {
  id: 2,
  title: "BPO & Operational Excellence",
  description: "Scale your operations with our specialized Business Process Outsourcing services. We combine technical precision with human-centric support to streamline your business growth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/bpo_operational_excellence_center.png"
}, {
  id: 3,
  title: "Digital Literacy & IT Mastery",
  description: "Bridge the digital divide with comprehensive IT training. From foundational computer skills to advanced software proficiency, we prepare your team for the modern digital landscape.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/it_mastery_digital_literacy_lab.png"
}];
const serviceFeatures = [{
  serviceId: 1,
  title: "Leadership Track",
  description: "Advanced management strategies for emerging and senior leaders.",
  icon: "MapPin"
}, {
  serviceId: 1,
  title: "Soft Skills",
  description: "Communication, emotional intelligence, and team collaboration.",
  icon: "Route"
}, {
  serviceId: 2,
  title: "Customer Support",
  description: "Omnichannel support solutions tailored to your brand voice.",
  icon: "Bell"
}, {
  serviceId: 2,
  title: "Data Management",
  description: "Secure and efficient processing of critical business information.",
  icon: "Wrench"
}, {
  serviceId: 3,
  title: "Software Training",
  description: "Hands-on workshops for industry-standard productivity tools.",
  icon: "CreditCard"
}, {
  serviceId: 3,
  title: "Cyber Security",
  description: "Essential protocols to protect your digital assets and data.",
  icon: "ChartBar"
}];
const iconMap: Record<string, React.ElementType> = {
  MapPin,
  Route,
  Bell,
  Wrench,
  CreditCard,
  ChartBar
};
function ServiceRow({
  service,
  index
}: {
  service: (typeof services)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const features = serviceFeatures.filter(f => f.serviceId === service.id);
  const isImageLeft = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className={`relative group ${isImageLeft ? "lg:order-1" : "lg:order-2"}`}>
        {}
        <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        
        <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-shadow-color/20 border border-border/50 bg-muted/30">
          <div className="absolute inset-0 bg-gradient-to-tr from-background/40 to-transparent z-10 pointer-events-none" />
          <img src={service.image} alt={service.title} className="w-full object-cover aspect-[4/3] transform group-hover:scale-105 transition-transform duration-700 ease-in-out" />
        </div>
      </div>

      {}
      <div className={`flex flex-col ${isImageLeft ? "lg:order-2" : "lg:order-1"}`}>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-secondary/10 text-secondary text-sm font-medium mb-6 w-fit">
          <Zap className="size-4" />
          <span className="font-mono tracking-wider">EST. 2018</span>
        </div>
        
        <h3 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-6 leading-tight tracking-tighter">
          {service.title}
        </h3>
        
        <p className="text-lg text-muted-foreground mb-10 leading-relaxed font-default text-left">
          {service.description}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {features.map((feature, fIndex) => {
          const Icon = iconMap[feature.icon] || ShieldCheck;
          return <Card key={fIndex} data-index={fIndex} className={`bg-card/40 backdrop-blur-xl border-white/10 shadow-md hover:shadow-xl hover:border-secondary/30 transition-all duration-300 group/card rounded-lg ${service.id === 1 ? "text-left" : ""}`}>
                <CardHeader className="pb-2 pt-5 px-5">
                  <div className="size-10 rounded-md bg-secondary/10 flex items-center justify-center mb-3 group-hover/card:bg-secondary group-hover/card:text-secondary-foreground transition-colors duration-300">
                    <Icon className="size-5 text-secondary group-hover/card:text-secondary-foreground" />
                  </div>
                  <CardTitle className="text-base font-default font-bold tracking-tight">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <CardDescription className="text-sm text-muted-foreground font-default">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        <div>
          <Button asChild variant="default" size="lg" className="rounded-md group bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg">
            <Link to={service.id === 2 ? "/BpoServicesPage" : "/TrainingAcademyPage"}>
              Explore {service.title.split(" ").pop()} Modules
              <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function ManagementSolutions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section data-section-id="234886" id="our-training-portfolio" className="relative py-24 md:py-32 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 md:mb-32 transition-all duration-700 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-6xl font-bold font-default text-foreground mb-6 tracking-tighter">
            Our Training <span className="text-secondary">Portfolio</span>
          </h2>
          <p className="text-xl text-muted-foreground font-default leading-relaxed">
            A comprehensive directory of skills development and business process 
            solutions designed to elevate your team's performance and operational efficiency.
          </p>
        </div>

        {}
        <div className="flex flex-col gap-24 md:gap-40">
          {services.map((service, index) => <ServiceRow key={service.id} service={service} index={index} />)}
        </div>
      </div>
    </section>;
}